import { useState } from 'react';
import { useRecoilValue, useSetRecoilState } from 'recoil';
import {
    fetchDownloadDicomSeries,
    fetchRequestImageDownload,
    useRegistrationTaskProcessing,
} from '~/backend/connector-api/connector-components';
import { withRetryOn202 } from '~/backend/connector-api/withRetryOn202';
import useBusyIndicator from '~/hooks/useBusyIndicator';
import useProcessDicomFiles from '~/hooks/useProcessDicomFiles';
import useViewportEnvironment from '~/hooks/useViewportEnvironment';
import { Job } from '~/state/atoms/jobAtoms';
import { selectedPatientIdState } from '~/state/atoms/patientAtoms';
import { workflowModalState, WorkflowTypes } from '~/state/atoms/viewerAtoms';
import { handleError } from '~/utils/errors';
import { PredictionModel } from '~/utils/transforms/predictionModelsDataTransforms';
import { transformTaskToGenerationModel } from '~/utils/transforms/taskTransforms';
import { NewJobData, useJobManagement } from '../../useJobManagement';
import useEnsureImageCached from '../useEnsureImageCached';
import useStructureSetCacheUpdate from '../useStructureSetCacheUpdate';

export type ConnectorContourPropagationTaskInput = {
    selectedModel?: PredictionModel;
    selectedStructures?: string[];
    selectedCustomization?: string;
    patientId: string;
    sourceImageStudyUid?: string;
    sourceImageSeriesUid?: string;
    sourceStructureSetUid?: string;
    targetImageStudyUid?: string;
    targetImageSeriesUid?: string;
    targetImageModality?: string;
};

/**
 * A hook for creating a contour propagation task via Connector API.
 * @returns {Object} - An object containing the start function and submission state.
 */
export default function useContourPropagationTask() {
    const selectedPatientId = useRecoilValue(selectedPatientIdState);
    const setWorkflowModalState = useSetRecoilState(workflowModalState);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const { mutateAsync: startRegistration } = useRegistrationTaskProcessing();
    const { processImportedFiles } = useProcessDicomFiles();
    const { ensureCached } = useEnsureImageCached();
    const updateCachedStructureSet = useStructureSetCacheUpdate();
    const { addJob, updateJob } = useJobManagement();
    const { processCreatedFiles } = useProcessDicomFiles();
    const { setEnvironmentStructureSet } = useViewportEnvironment();
    const { showBusy, hideBusy } = useBusyIndicator();

    const handleProcessedFiles = async (job: Job) => {
        if (job.results && job.results?.length > 0) {
            const generationModel = transformTaskToGenerationModel(job.task);
            const processedItems = await processCreatedFiles(
                job.results,
                generationModel
            );
            if (processedItems) {
                updateJob(job.id, { isImported: true });
                setEnvironmentStructureSet(
                    processedItems.structureSets,
                    selectedPatientId ?? ''
                );
            }
        }
    };

    /**
     * Creates the contour propagation task with the provided input.
     * @param {ConnectorContourPropagationTaskInput} input - The input for the task.
     */
    const start = async (input: ConnectorContourPropagationTaskInput) => {
        if (isSubmitting) {
            return;
        }

        if (!input.selectedModel) {
            handleError(new Error('No model selected.'));
            return;
        }
        if (!input.sourceImageSeriesUid || !input.targetImageSeriesUid) {
            handleError(
                new Error('Source or target image series UID missing.')
            );
            return;
        }
        if (!input.sourceImageStudyUid || !input.targetImageStudyUid) {
            handleError(new Error('Source or target image study UID missing.'));
            return;
        }

        setIsSubmitting(true);
        try {
            // Make sure the connector actually has the source image
            // before asking it to register anything — the series may
            // have been generated locally (e.g. by Image+) and never
            // round-tripped through the connector cache. The target
            // image is separately fetched via C-MOVE below.
            await ensureCached({
                patientId: input.patientId,
                seriesInstanceUID: input.sourceImageSeriesUid!,
                studyInstanceUID: input.sourceImageStudyUid!,
            });

            await updateCachedStructureSet();

            // Ensure the destination image is available in the
            // connector before submitting the registration task.
            // In session-based workflows the connector only has
            // the primary image; the target must be retrieved
            // via DICOM C-MOVE first.
            await showBusy('Downloading target image...');
            let targetImageBlob: Blob | undefined;
            try {
                await fetchRequestImageDownload({
                    body: {
                        patientId: input.patientId,
                        studyInstanceUID: input.targetImageStudyUid!,
                        seriesInstanceUID: input.targetImageSeriesUid!,
                        modality: input.targetImageModality,
                    },
                });

                targetImageBlob = await withRetryOn202(
                    async () =>
                        await fetchDownloadDicomSeries({
                            pathParams: {
                                studyId: input.targetImageStudyUid!,
                                seriesId: input.targetImageSeriesUid!,
                            },
                            queryParams: { series: true },
                        })
                );
            } finally {
                hideBusy();
            }

            const modelIdentifier = input.selectedCustomization
                ? `${input.selectedModel.name}.${input.selectedCustomization}`
                : input.selectedModel.name;

            const task = await startRegistration({
                body: {
                    modelName: modelIdentifier,
                    sourceImageSeries: {
                        patientID: input.patientId,
                        seriesInstanceUID: input.sourceImageSeriesUid,
                        studyInstanceUID: input.sourceImageStudyUid,
                    },
                    destinationImageSeries: {
                        patientID: input.patientId,
                        seriesInstanceUID: input.targetImageSeriesUid,
                        studyInstanceUID: input.targetImageStudyUid,
                    },
                    structureSet: {
                        sopInstanceUID: input.sourceStructureSetUid,
                        patientID: input.patientId,
                    },
                },
            });

            const newJob: NewJobData = {
                name: `Adapt+`,
                id: task.id,
                type: WorkflowTypes.ADAPT,
                task: {
                    model: input.selectedModel,
                    output: task,
                },
                onLoad: handleProcessedFiles,
            };

            addJob(newJob);
            setWorkflowModalState((state) => ({
                ...state,
                open: false,
            }));

            // Load the already-downloaded destination image into the UI
            if (targetImageBlob) {
                const files = [
                    new File(
                        [targetImageBlob],
                        `${input.targetImageSeriesUid}.zip`
                    ),
                ];
                processImportedFiles(files).catch((err) =>
                    handleError(
                        err,
                        `Couldn't process DICOM files for ${input.targetImageSeriesUid}.`
                    )
                );
            }
        } catch (error) {
            handleError(error, 'Failed to start contour propagation task.');
        } finally {
            setIsSubmitting(false);
        }
    };

    return { start, isSubmitting };
}
